import { useStytchMember } from "@stytch/react/b2b";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useState } from "react";
import { DownloadCloud, Trash2, Plus, MoreHorizontal } from "lucide-react";
import moment from "moment-timezone";
import { Link } from "react-router-dom";
import { Textarea } from "../../components/ui/textarea";
import {
  useDownloadTicketBatch,
  useTicketProviderSummary,
  useDownloadUndownloadedTickets,
  useDownloadUndownloadedUpdatedTickets,
} from "../../lib/api/tickets";
import {
  TicketRunSummary,
  TicketsSummary,
  Provider,
} from "../../lib/models/index";
import {
  TicketProviderSummary,
  TicketProviderRecentRunPartial,
  TicketProviderBatchDownloadPartial,
} from "../../lib/models/tickets";
import { ProviderType } from "../../lib/models/provider";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { SelectInput } from "../../components/ui/SelectInput";
import { Skeleton } from "../../components/ui/skeleton";
import { useToast } from "../../components/ui/use-toast";
import { DataTable, DataTableProps } from "../../components/ui/dataTable";
import { downloadCsvTextAsFile } from "../../lib/browser/download";
import { downloadFromS3 } from "../../lib/file";
import { MultiSelect } from "../../components/ui/selectMulti";

export function TicketDashboard() {
  const summaryRes = useTicketProviderSummary(ProviderType.Weight);

  if ([summaryRes].some((result) => result.isLoading)) {
    return (
      <div className="flex flex-col space-y-2">
        <Skeleton className="h-4 w-[250px]" />
        <Skeleton className="h-4 w-[200px]" />
      </div>
    );
  }

  if ([summaryRes].some((result) => result.error || !result?.data?.success)) {
    return (
      <div className="flex flex-col space-y-2">
        Failed to fetch ticket info. Please refresh and try again
      </div>
    );
  }

  return <TicketDashboardView summaries={summaryRes?.data?.data || []} />;
}

function LastSummaryTicketRun({
  runs,
}: {
  runs: TicketProviderRecentRunPartial[];
}) {
  if (!runs.length) {
    return <span>Never Run</span>;
  }

  const run = runs[0];
  return <span>{moment(run.fetched).format("M/D/YY HH:mm a")}</span>;
}

function LastSummaryBatchDownloaded({
  batches,
}: {
  batches: TicketProviderBatchDownloadPartial[];
}) {
  const { member } = useStytchMember();

  if (!batches.length) {
    return <span>Never Downloaded</span>;
  }

  const isAdmin = member?.trusted_metadata["role"] === "admin" || false;
  const batch = batches[0];
  return (
    <span>
      {isAdmin && <>{batch.user.name} on </>}
      {moment(batch.downloaded_date).format("M/D/YY HH:mm a")}
    </span>
  );
}

export function TicketDashboardView({
  summaries,
}: {
  summaries: TicketProviderSummary[];
}) {
  const { toast } = useToast();
  const [multiProviderValues, setMultiProviderValues] = useState<string[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const downloadProviderTickets = useDownloadUndownloadedTickets(
    ProviderType.Weight,
    "new",
  );

  const onDownload = async (providerIds: string[], filename: string) => {
    if (isDownloading) return;
    setIsDownloading(true);

    const result = await downloadProviderTickets(providerIds);
    setIsDownloading(false);

    if (!result.success) {
      toast({
        title: "Failed to download tickets",
        description: "",
      });
    } else {
      toast({
        title: "Downloaded",
        description: "",
      });
      downloadCsvTextAsFile(filename, result.data.ticketsToDownload);
    }
  };

  const dashboardColumns: ColumnDef<any>[] = [
    {
      id: "provider",
      header: "Provider",
      cell: ({ row }) => <span className="">{row.original.provider.alias_name || row.original.provider.name}</span>,
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "lastUpdated",
      header: "Last Updated",
      cell: ({ row }) => (
        <LastSummaryTicketRun runs={row.original.recentRuns} />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "netNewCount",
      header: "Net New",
      cell: ({ row }) => {
        return <span className="">{row.original.netNewCount}</span>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "updatedCount",
      header: "Updated",
      cell: ({ row }) => {
        return <span className="">{row.original.updatedCount}</span>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "downloaded",
      header: "Last Downloaded",
      cell: ({ row }) => (
        <LastSummaryBatchDownloaded
          batches={row.original.recentBatchDownloads}
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: "newActions",
      header: "Download New",
      cell: ({ row }) => (
        <div className="flex flex-row">
          <DownloadProviderTickets
            provider={row.original.provider}
            downloadType="new"
          />
        </div>
      ),
    },
    {
      id: "updatedActions",
      header: "Download Updated",
      cell: ({ row }) => (
        <div className="flex flex-row">
          <DownloadProviderTickets
            provider={row.original.provider}
            downloadType="updated"
          />
        </div>
      ),
    },
  ];

  const providerList = summaries.map((item) => ({
    value: item.provider.id,
    label: item.provider.alias_name || item.provider.name,
  }));
  const fetchDate = moment().format("MM/DD/YY");

  const sortedSummaries = summaries.sort((a,b) => {
    const aName = a.provider.alias_name || a.provider.name;
    const bName = b.provider.alias_name || b.provider.name;
    if (aName === "Ticket Splits") return -1;
    if (bName === "Ticket Splits") return 1;

    return aName.localeCompare(bName)
  });

  return (
    <>
      <div className="container flex flex-col mb-2">
        <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
          Weight Tickets Dashboard
        </h1>
      </div>
      <div className="flex items-start flex-col h-full">
        <div className="container flex flex-col ">
          <div className="flex flex-row justify-between">
            <MultiSelect
              options={providerList}
              values={multiProviderValues}
              setValues={setMultiProviderValues}
            />
            <Button
              variant="ghost"
              className="h-8 w-8 p-0"
              onClick={() =>
                onDownload(
                  multiProviderValues,
                  `${multiProviderValues.length}_weight_new_${fetchDate}.csv`,
                )
              }
              disabled={isDownloading}
            >
              <span className="sr-only">Download</span>
              <DownloadCloud className="h-4 w-4" />
            </Button>
          </div>

          <DataTable columns={dashboardColumns} data={sortedSummaries} />
          <RecentDownloadsExpanded summaries={summaries} />
        </div>
      </div>
    </>
  );
}

function DownloadProviderTickets({
  provider,
  downloadType,
}: {
  provider: Provider;
  downloadType: "new" | "updated";
}) {
  const { toast } = useToast();
  const [isDownloading, setIsDownloading] = useState(false);
  const downloadUpdatedTickets = useDownloadUndownloadedUpdatedTickets();
  const downloadProviderTickets = useDownloadUndownloadedTickets(
    ProviderType.Weight,
    downloadType,
  );

  const onDownload = async (providerIds: string[], filename: string) => {
    if (isDownloading) return;
    setIsDownloading(true);


    const result = await (downloadType === "updated" ? downloadUpdatedTickets(providerIds) : downloadProviderTickets(providerIds));
    setIsDownloading(false);

    if (!result.success) {
      toast({
        title: "Failed to download tickets",
        description: "",
      });
    } else {
      toast({
        title: "Downloaded",
        description: "",
      });
      if (downloadType !== "updated") {
        downloadCsvTextAsFile(`${filename}.csv`, result.data.ticketsToDownload);
      } else {
        downloadFromS3(result.data, `${filename}.xlsx`)
      }
    }
  };

  const fetchDate = moment().format("MM/DD/YY");

  return (
    <Button
      variant="ghost"
      className="h-8 w-8 p-0"
      onClick={() =>
        onDownload(
          [provider.id],
          `${provider.name}_${downloadType}_${fetchDate}`,
        )
      }
      disabled={isDownloading}
    >
      <span className="sr-only">Download</span>
      <DownloadCloud className="h-4 w-4" />
    </Button>
  );
}

function TicketRunSummaryAccordionLabel({
  ticketRunSummary,
}: {
  ticketRunSummary: TicketRunSummary;
}) {
  if (!ticketRunSummary.records.length) {
    return (
      <p>
        {ticketRunSummary.provider.name} |{" "}
        <span className="font-light text-sm">No Runs Yet</span>
      </p>
    );
  }

  return (
    <p>
      {ticketRunSummary.provider.name} |{" "}
      <span className="font-light text-sm">
        {ticketRunSummary.records.length} of{" "}
        {ticketRunSummary.metaData.pagination.total_count} Runs
      </span>
    </p>
  );
}

function RecentDownloadsExpanded({
  summaries,
}: {
  summaries: TicketProviderSummary[];
}) {
  if (
    !summaries.length ||
    !summaries.some(
      (summary: TicketProviderSummary) => !!summary.recentBatchDownloads.length,
    )
  ) {
    return null;
  }

  return (
    <>
      <h2 className="mt-4 scroll-m-20 text-lg font-bold tracking-tight lg:text-2xl">
        Recent Downloads
      </h2>
      <Accordion type="single" collapsible className="w-full">
        {summaries
          .filter((summary) => {
            return !!summary.recentBatchDownloads.length;
          })
          .map((summary) => (
            <ProviderDownloadsSummary
              key={`DownloadSummary:${summary.provider.id}`}
              summary={summary}
            />
          ))}
      </Accordion>
    </>
  );
}

function ProviderDownloadsSummary({
  summary,
}: {
  summary: TicketProviderSummary;
}) {
  const dashboardColumns: ColumnDef<any>[] = [
    {
      accessorKey: "downloaded",
      header: "Downloaded",
      cell: ({ row }) => (
        <LastSummaryBatchDownloaded batches={[row.original]} />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "newNew",
      header: "Ticket Count",
      cell: ({ row }) => {
        return <span className="">{row.original.ticketCount}</span>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: "actions",
      header: "Download",
      cell: ({ row }) => (
        <span className="flex flex-row">
          <DownloadExistingBatchTickets
            provider={summary.provider as any}
            batchId={row.original.id}
          />
        </span>
      ),
    },
  ];

  return (
    <AccordionItem
      key={`ProviderDownloadSummary:${summary.provider.id}`}
      value={summary.provider.name}
    >
      <AccordionTrigger>{summary.provider.name}</AccordionTrigger>
      <AccordionContent>
        <DataTable
          columns={dashboardColumns}
          data={summary.recentBatchDownloads}
        />
      </AccordionContent>
    </AccordionItem>
  );
}

function DownloadExistingBatchTickets({
  provider,
  batchId,
}: {
  provider: Provider;
  batchId: string;
}) {
  const { toast } = useToast();
  const [isDownloading, setIsDownloading] = useState(false);
  const downloadProviderTickets = useDownloadTicketBatch(
    provider.id,
    batchId,
    ProviderType.Weight,
  );

  const onDownload = async (providerId: string) => {
    if (isDownloading) return;
    setIsDownloading(true);

    const result = await downloadProviderTickets(provider.id);
    setIsDownloading(false);

    if (!result.success) {
      toast({
        title: "Failed to download tickets",
        description: "",
      });
    } else {
      const fetchDate = moment(result.data.downloaded_date).format("MM/DD/YY");
      toast({
        title: "Downloaded",
        description: "",
      });

      downloadCsvTextAsFile(
        `${provider.name}_${fetchDate}.csv`,
        result.data.ticketsToDownload,
      );
    }
  };

  return (
    <Button
      variant="ghost"
      className="h-8 w-8 p-0"
      onClick={() => onDownload(provider.id)}
      disabled={isDownloading}
    >
      <span className="sr-only">Download</span>
      <DownloadCloud className="h-4 w-4" />
    </Button>
  );
}
